import { notify } from '@affine/component';
import {
  generateUrl,
  type UseSharingUrl,
} from '@affine/core/components/hooks/affine/use-share-url';
import { EditorService } from '@affine/core/modules/editor';
import { I18n } from '@affine/i18n';
import { track } from '@affine/track';
import type {
  MenuOptions,
  SimpleTableBlockModel,
} from '@blocksuite/affine/blocks';
import {
  menu,
  type MenuConfig,
} from '@blocksuite/affine-components/context-menu';
import { LinkIcon } from '@docnboard/icons/blocksuite/lit';
import { getBaseUrl } from '@docnboard/rest';
import type { FrameworkProvider } from '@toeverything/infra';

export function createSimpleTableOptionsConfig(framework: FrameworkProvider) {
  return {
    configure: (model: SimpleTableBlockModel, options: MenuOptions) => {
      const items = options.items;

      items.splice(2, 0, createCopyLinkToBlockMenuItem(framework, model));

      return options;
    },
  };
}

function createCopyLinkToBlockMenuItem(
  framework: FrameworkProvider,
  model: SimpleTableBlockModel
): MenuConfig {
  return menu.action({
    name: I18n['docnboard.database.actions.copy-link-to-block'](),
    prefix: LinkIcon({ width: '20', height: '20' }),
    hide: () => {
      const { editor } = framework.get(EditorService);
      const mode = editor.mode$.value;
      return mode === 'edgeless';
    },
    select: () => {
      const baseUrl = getBaseUrl();
      if (!baseUrl) return;

      const pageId = model.doc.id;
      const { editor } = framework.get(EditorService);
      const mode = editor.mode$.value;

      if (mode === 'edgeless') return;

      const workspaceId = editor.doc.workspace.id;
      const options: UseSharingUrl = {
        workspaceId,
        pageId,
        shareMode: mode,
        blockIds: [model.id],
      };

      const str = generateUrl(options);
      if (!str) return;

      const type = model.flavour;
      const page = editor.editorContainer$.value;

      page?.host?.std.clipboard
        .writeToClipboard(items => {
          items['text/plain'] = str;
          // wrap a link
          items['text/html'] = `<a href="${str}">${str}</a>`;
          return items;
        })
        .then(() => {
          track.doc.editor.toolbar.copyBlockToLink({ type });
          notify.success({ title: I18n['Copied link to clipboard']() });
        })
        .catch(console.error);
    },
  });
}
